import { useEffect, useRef, useState } from 'react'
import { apiClient } from '../../lib/api/client'
import { problemTitle } from '../parts/usePartsLibrary'
import type { Rfq } from '../rfq/domain'

/**
 * UID-300 — the data-layer seam for the Workspace detail page's RFQs tab.
 * `RfqListPage.tsx` calls `apiClient.GET('/rfq')` directly (no injectable
 * service), so this is a thin, workspace-scoped WRAPPER around the same
 * `GET /rfq` call — NOT a reimplementation of its fetch/error logic —
 * passing `?workspaceId=` through so the BFF returns only this workspace's
 * slice. Injectable for tests, mirroring `PartsLibraryService`'s seam.
 *
 * UID-603 (ADR-0176 D6 row 4/D7.2) — this caller keeps passing NO `limit`
 * (and never will: `workspaceId` + `limit` together is a 400, D7.2 — the
 * workspace filter is applied in-process over `lines`, so pagination would
 * silently produce arbitrarily-short pages). Once `/rfq` converts to the
 * envelope, an unparameterized-`limit` request still returns
 * `{ items, nextCursor, totalCount }` (D1.2/D1.3) — this is behaviourally
 * unchanged apart from reading `data.items` instead of a bare array.
 */
export interface WorkspaceRfqsService {
  listRfqs(workspaceId: string): Promise<readonly Rfq[]>
}

/**
 * Calls the SAME `GET /rfq` endpoint `RfqListPage` uses, with `workspaceId`
 * added to the query. Fully generated-type driven — no cast: the landed
 * contract's response IS the `{ items, nextCursor, totalCount, statusCounts }`
 * envelope, so `data.items` type-checks directly.
 */
async function bffListRfqs(workspaceId: string): Promise<readonly Rfq[]> {
  const { data, error } = await apiClient.GET('/rfq', {
    params: { query: { workspaceId } },
  })
  if (error || !data) {
    throw new Error(problemTitle(error, "We couldn't load this project's RFQs. Try again."))
  }
  // UID-603 (ADR-0176 D1.3/D6 #4) — `data.items`, and NOTHING ELSE CHANGES here.
  //
  // This caller deliberately passes no `limit`: ADR-0176 D7.2 makes
  // `?workspaceId` + `limit` a 400, because the project filter is applied
  // IN PROCESS over each RFQ's `lines` jsonb (no index, migration 2000), so a
  // paginated page would be arbitrarily short. So this is the unbounded path and
  // is behaviourally identical to before — only the envelope moved.
  //
  // `WorkspacePage`'s header stat card counts these rows. Unpaginated,
  // `totalCount` equals `items.length`, so reading either is correct TODAY;
  // whichever it reads must switch to `totalCount` if ADR-0176 R5 ever lifts the
  // 400 and paginates this view.
  return data.items
}

export const defaultWorkspaceRfqsService: WorkspaceRfqsService = {
  listRfqs: bffListRfqs,
}

export type WorkspaceRfqsState =
  | { readonly status: 'loading' }
  | { readonly status: 'error'; readonly message: string }
  | { readonly status: 'ready'; readonly rfqs: readonly Rfq[] }

export interface UseWorkspaceRfqsResult {
  readonly state: WorkspaceRfqsState
}

/**
 * Loads this workspace's RFQ slice through a `WorkspaceRfqsService`
 * (defaults to the real BFF-backed `defaultWorkspaceRfqsService`). Called
 * unconditionally alongside `useWorkspace`/`usePartsLibrary` in
 * `WorkspacePage.tsx` (Rules of Hooks — that component has early returns) —
 * this ALSO means the RFQ count is available for the header stat cards even
 * before the RFQs tab is ever visited, matching how `usePartsLibrary`
 * already backs the Parts/BOMs tab badges.
 */
export function useWorkspaceRfqs(
  workspaceId: string,
  service: WorkspaceRfqsService = defaultWorkspaceRfqsService,
): UseWorkspaceRfqsResult {
  const [state, setState] = useState<WorkspaceRfqsState>({ status: 'loading' })
  const serviceRef = useRef(service)
  serviceRef.current = service

  useEffect(() => {
    let cancelled = false
    setState({ status: 'loading' })

    serviceRef.current
      .listRfqs(workspaceId)
      .then((rfqs) => {
        if (!cancelled) setState({ status: 'ready', rfqs })
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({
            status: 'error',
            message:
              error instanceof Error
                ? error.message
                : "We couldn't load this project's RFQs. Try again.",
          })
        }
      })

    return () => {
      cancelled = true
    }
  }, [workspaceId])

  return { state }
}
